import React, { useEffect, useRef, useState } from 'react';

interface Props {
  onDone: (dataUrl: string) => void;
  onCancel: () => void;
}

/**
 * Potpis kupca prstom na ekranu.
 *
 * Interni dokaz o isporuci, bez poreske funkcije. Crta se na canvasu i pretvara
 * u sliku koja ide u red za slanje kao i svaki drugi zapis — potpis dat bez
 * signala mora da preživi do sledeće veze.
 */
export default function SignaturePad({ onDone, onCancel }: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawing = useRef(false);
  const [hasInk, setHasInk] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Canvas mora dobiti stvarnu piksel-veličinu, inače je linija mutna i
    // pomerena u odnosu na prst na telefonima sa gustim ekranom.
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;

    const context = canvas.getContext('2d');
    if (!context) return;

    context.scale(ratio, ratio);
    context.lineWidth = 2.5;
    context.lineCap = 'round';
    context.lineJoin = 'round';
    context.strokeStyle = '#1c2430';
  }, []);

  const positionOf = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };

  const start = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const context = canvasRef.current?.getContext('2d');
    if (!context) return;

    event.currentTarget.setPointerCapture(event.pointerId);
    drawing.current = true;
    setHasInk(true);

    const { x, y } = positionOf(event);
    context.beginPath();
    context.moveTo(x, y);
  };

  const move = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;

    const context = canvasRef.current?.getContext('2d');
    if (!context) return;

    const { x, y } = positionOf(event);
    context.lineTo(x, y);
    context.stroke();
  };

  const stop = () => {
    drawing.current = false;
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');
    if (!canvas || !context) return;

    context.clearRect(0, 0, canvas.width, canvas.height);
    setHasInk(false);
  };

  return (
    <div className="signature">
      <p className="signature__title">Potpis kupca</p>

      <canvas
        ref={canvasRef}
        className="signature__canvas"
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={stop}
        onPointerLeave={stop}
      />

      <div className="signature__actions">
        <button type="button" onClick={onCancel}>Odustani</button>
        <button type="button" onClick={clear} disabled={!hasInk}>Obriši</button>
        <button
          type="button"
          className="primary"
          disabled={!hasInk}
          onClick={() => {
            const url = canvasRef.current?.toDataURL('image/png');
            if (url) onDone(url);
          }}
        >
          Sačuvaj potpis
        </button>
      </div>
    </div>
  );
}
